/**
 * Quake 3 style player movement — port of the id Tech 3 pmove essentials.
 * Ground accelerate, air accelerate (strafe jumping works), friction,
 * jump, knockback (rocket/lightning jumps), AABB world collision with
 * step-up. Units: meters, m/s. Tuned so it *feels* like Q3 at 1:32 scale.
 */

import * as THREE from 'three';
import { Box, SolidWorld } from './map';

export const PM = {
  gravity: 26,
  groundSpeed: 9.5,      // ~320 ups
  airSpeed: 9.5,
  groundAccel: 10,
  airAccel: 1.0,         // Q3 default air accel
  airControlAccel: 30,   // CPM-style small air control for feel
  friction: 6,
  stopSpeed: 3,
  jumpVelocity: 8.8,     // ~270 ups
  stepHeight: 0.45,
  playerRadius: 0.42,
  playerHeight: 1.75,
  eyeHeight: 1.6,
  crouchHeight: 1.1,
};

export interface MoveInput {
  forward: number; // -1..1
  right: number;   // -1..1
  jump: boolean;
  crouch: boolean;
}

export class QuakePlayer {
  public position = new THREE.Vector3();
  public velocity = new THREE.Vector3();
  public yaw = 0;
  public pitch = 0;
  public onGround = false;
  public jumpHeld = false;
  public crouching = false;

  private tmp = new THREE.Vector3();
  private wishdir = new THREE.Vector3();
  /** candidats de la broadphase, réutilisé à chaque test (zéro allocation) */
  private cand: Box[] = [];

  get eyeHeight(): number { return this.crouching ? PM.crouchHeight - 0.15 : PM.eyeHeight; }
  get height(): number { return this.crouching ? PM.crouchHeight : PM.playerHeight; }

  // ---------------------------------------------------------------- pmove

  update(dt: number, input: MoveInput, world: SolidWorld): void {
    this.crouching = input.crouch;

    // wish direction in world space from yaw (camera forward projected on ground)
    const fx = -Math.sin(this.yaw), fz = -Math.cos(this.yaw);
    const rx = Math.cos(this.yaw), rz = -Math.sin(this.yaw);
    this.wishdir.set(fx * input.forward + rx * input.right, 0, fz * input.forward + rz * input.right);
    const wishspeed0 = Math.min(1, this.wishdir.length());
    if (wishspeed0 > 0) this.wishdir.divideScalar(this.wishdir.length());

    if (this.onGround) {
      // Jump
      if (input.jump && !this.jumpHeld) {
        this.velocity.y = PM.jumpVelocity;
        this.onGround = false;
        this.jumpHeld = true;
        this.airMove(dt, wishspeed0);
      } else {
        this.groundMove(dt, wishspeed0);
      }
    } else {
      this.airMove(dt, wishspeed0);
    }
    if (!input.jump) this.jumpHeld = false;

    // Gravity
    if (!this.onGround) this.velocity.y -= PM.gravity * dt;

    // Integrate with collision (3 axes separately = slide).
    // Anti-tunneling : à grande vitesse (rocket jump, pad) on sous-échantillonne
    // pour ne pas traverser les garde-corps fins (0,14 m d'épaisseur).
    const travel = this.velocity.length() * dt;
    const sub = travel > 0.4 ? Math.min(4, Math.ceil(travel / 0.4)) : 1;
    const sdt = dt / sub;
    for (let i = 0; i < sub; i++) this.slideMove(sdt, world);
  }

  private applyFriction(dt: number): void {
    const speed = Math.hypot(this.velocity.x, this.velocity.z);
    if (speed < 0.001) { this.velocity.x = 0; this.velocity.z = 0; return; }
    const control = speed < PM.stopSpeed ? PM.stopSpeed : speed;
    const drop = control * PM.friction * dt;
    const newspeed = Math.max(0, speed - drop) / speed;
    this.velocity.x *= newspeed;
    this.velocity.z *= newspeed;
  }

  private accelerate(wishspeed: number, accel: number, dt: number): void {
    const currentspeed = this.velocity.x * this.wishdir.x + this.velocity.z * this.wishdir.z;
    const addspeed = wishspeed - currentspeed;
    if (addspeed <= 0) return;
    let accelspeed = accel * dt * wishspeed;
    if (accelspeed > addspeed) accelspeed = addspeed;
    this.velocity.x += accelspeed * this.wishdir.x;
    this.velocity.z += accelspeed * this.wishdir.z;
  }

  private groundMove(dt: number, wishspeed0: number): void {
    this.applyFriction(dt);
    const speed = this.crouching ? PM.groundSpeed * 0.5 : PM.groundSpeed;
    this.accelerate(wishspeed0 * speed, PM.groundAccel, dt);
    // stick to ground
    if (this.velocity.y < 0) this.velocity.y = 0;
  }

  private airMove(dt: number, wishspeed0: number): void {
    const wishspeed = wishspeed0 * PM.airSpeed;
    // Classic Q3 air accelerate → enables strafe jumping
    this.accelerate(wishspeed, PM.airAccel, dt);
    // Light CPM air control when only pressing forward (feel, not exploit)
    if (wishspeed0 > 0 && Math.abs(this.wishdir.x * this.velocity.z - this.wishdir.z * this.velocity.x) < 0.3) {
      this.airControl(dt);
    }
  }

  private airControl(dt: number): void {
    const speed = Math.hypot(this.velocity.x, this.velocity.z);
    if (speed < 0.01) return;
    const vx = this.velocity.x / speed, vz = this.velocity.z / speed;
    const dot = vx * this.wishdir.x + vz * this.wishdir.z;
    if (dot > 0) {
      const k = 32 * PM.airControlAccel * dot * dot * dt * 0.02;
      const nx = vx * speed + this.wishdir.x * k;
      const nz = vz * speed + this.wishdir.z * k;
      const nl = Math.hypot(nx, nz);
      this.velocity.x = (nx / nl) * speed;
      this.velocity.z = (nz / nl) * speed;
    }
  }

  // ---------------------------------------------------------------- collision

  /** Player AABB at a given position */
  private aabb(pos: THREE.Vector3, out: { min: THREE.Vector3; max: THREE.Vector3 }): void {
    out.min.set(pos.x - PM.playerRadius, pos.y, pos.z - PM.playerRadius);
    out.max.set(pos.x + PM.playerRadius, pos.y + this.height, pos.z + PM.playerRadius);
  }

  private box = { min: new THREE.Vector3(), max: new THREE.Vector3() };

  /** Premier solide recouvrant la boîte courante (test d'existence). */
  private overlaps(world: SolidWorld): Box | null {
    const b = this.box;
    for (const s of world.queryBox(b.min, b.max, this.cand)) {
      if (b.min.x < s.max.x && b.max.x > s.min.x &&
          b.min.y < s.max.y && b.max.y > s.min.y &&
          b.min.z < s.max.z && b.max.z > s.min.z) return s;
    }
    return null;
  }

  /**
   * Solide recouvrant dont le sommet est le plus haut (`top`) ou dont la base est
   * la plus basse (`bottom`). Rend la collision indépendante de l'ordre de
   * construction de la map : on retient le plancher réel, pas le premier trouvé.
   */
  private overlapsBest(world: SolidWorld, mode: 'top' | 'bottom'): Box | null {
    const b = this.box;
    let best: Box | null = null;
    for (const s of world.queryBox(b.min, b.max, this.cand)) {
      if (b.min.x < s.max.x && b.max.x > s.min.x &&
          b.min.y < s.max.y && b.max.y > s.min.y &&
          b.min.z < s.max.z && b.max.z > s.min.z) {
        if (!best) best = s;
        else if (mode === 'top' ? s.max.y > best.max.y : s.min.y < best.min.y) best = s;
      }
    }
    return best;
  }

  private slideMove(dt: number, world: SolidWorld): void {
    const pos = this.position;
    const vel = this.velocity;

    // --- Y axis : en chute on se pose sur le plancher LE PLUS HAUT, en montée
    // on bute sur le plafond LE PLUS BAS (et non sur le premier solide trouvé).
    pos.y += vel.y * dt;
    this.aabb(pos, this.box);
    const hit = this.overlapsBest(world, vel.y <= 0 ? 'top' : 'bottom');
    let grounded = false;
    if (hit) {
      if (vel.y <= 0) { pos.y = hit.max.y; grounded = true; vel.y = 0; }
      else { pos.y = hit.min.y - this.height - 0.001; vel.y = 0; }
    }

    // --- X axis (with step-up)
    this.moveAxis(pos, vel, dt, 'x', world);
    // --- Z axis
    this.moveAxis(pos, vel, dt, 'z', world);

    // Ground check (probe slightly below)
    if (!grounded) {
      this.tmp.copy(pos); this.tmp.y -= 0.06;
      this.aabb(this.tmp, this.box);
      const g = this.overlapsBest(world, 'top');
      grounded = !!g && vel.y <= 0.01;
      if (grounded && g) { pos.y = g.max.y; vel.y = 0; }
    }
    this.onGround = grounded;
  }

  private moveAxis(pos: THREE.Vector3, vel: THREE.Vector3, dt: number, axis: 'x' | 'z', world: SolidWorld): void {
    const before = pos[axis];
    pos[axis] += vel[axis] * dt;
    this.aabb(pos, this.box);
    // marche la plus haute parmi les bloquants : sur une rampe le joueur chevauche
    // deux marches, il faut monter sur la plus haute sinon il accroche.
    const hit = this.overlapsBest(world, 'top');
    if (!hit) return;

    // Try step-up when on ground / nearly on ground
    if (this.onGround || vel.y <= 0.5) {
      const stepY = hit.max.y - pos.y;
      if (stepY > 0 && stepY <= PM.stepHeight) {
        this.tmp.copy(pos); this.tmp.y = hit.max.y + 0.001;
        this.aabb(this.tmp, this.box);
        if (!this.overlaps(world)) { pos.y = this.tmp.y; return; }
      }
    }
    // Block: push out along axis — on retient le bloquant le plus contraignant
    // (résultat déterministe, pas de pénétration résiduelle dans un voisin).
    this.aabb(pos, this.box); // le test de step-up a pu laisser une boîte décalée
    let push = hit;
    const b = this.box;
    for (const s of world.queryBox(b.min, b.max, this.cand)) {
      if (b.min.x < s.max.x && b.max.x > s.min.x &&
          b.min.y < s.max.y && b.max.y > s.min.y &&
          b.min.z < s.max.z && b.max.z > s.min.z) {
        if (vel[axis] > 0 ? s.min[axis] < push.min[axis] : s.max[axis] > push.max[axis]) push = s;
      }
    }
    if (vel[axis] > 0) pos[axis] = push.min[axis] - PM.playerRadius - 0.001;
    else pos[axis] = push.max[axis] + PM.playerRadius + 0.001;
    vel[axis] = 0;
    // Safety: if still overlapping (corner case), revert
    this.aabb(pos, this.box);
    if (this.overlaps(world)) pos[axis] = before;
  }

  // ---------------------------------------------------------------- helpers

  /** Knockback / jump pad: add velocity (Q3 style) */
  addVelocity(v: THREE.Vector3): void {
    this.velocity.add(v);
    this.onGround = false;
  }

  /** Jump pad: set velocity outright (pads in Q3 override) */
  setVelocity(v: THREE.Vector3): void {
    this.velocity.copy(v);
    this.onGround = false;
  }

  teleport(pos: THREE.Vector3, yaw: number): void {
    this.position.copy(pos);
    this.yaw = yaw;
    this.velocity.set(0, 0, 0);
    this.onGround = false;
  }

  eye(out: THREE.Vector3): THREE.Vector3 {
    return out.set(this.position.x, this.position.y + this.eyeHeight, this.position.z);
  }

  forward(out: THREE.Vector3): THREE.Vector3 {
    const cp = Math.cos(this.pitch);
    return out.set(-Math.sin(this.yaw) * cp, Math.sin(this.pitch), -Math.cos(this.yaw) * cp).normalize();
  }

  speed2D(): number {
    return Math.hypot(this.velocity.x, this.velocity.z);
  }
}